import React, { FC, useState } from 'react';
import { DateHeader } from '~/components';
import { DateHeaderStyled, CrewListContainerStyled, CrewHeadingStyled, CrewListStyled } from './styled';
import { Box, IconSize, Heading, Icon, Text, Spinner, Button, SpinnerSize } from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { SearchField, useAppUser } from '@bechtel/client-common';
import { CrewMembersProps } from './types';
import { GET_TIMESHEET_STATUS_BY_SUPERVISOR } from '@bechtel/client-gql';
import { PersonaType, TimesheetBySupervisorInput, TimesheetStatusCrewMember } from '@bechtel/types';
import { useQuery, NetworkStatus } from '@apollo/client';
import { formatISO } from 'date-fns';
import { find, get, includes, map, some, toLower, trim } from 'lodash';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
import { TimesheetCrewSupervisorItem } from './crew-supervisor';
import { TimesheetCrewItem } from './crew-item';

export const CrewMembers: FC<CrewMembersProps> = ({ showDateHeader, forDate, setForDate }) => {
  const { user } = useAppUser();
  const [searchQuery, setSearchQuery] = useState('');

  const { loading, error, data, called, refetch, networkStatus } = useQuery<
    {
      timesheetStatusCrewMembers: TimesheetStatusCrewMember[];
    },
    {
      input: TimesheetBySupervisorInput;
    }
  >(GET_TIMESHEET_STATUS_BY_SUPERVISOR, {
    variables: {
      input: {
        persona: user?.crewMember.persona,
        crewMemberId: user?.crewMember.id,
        forDate: formatISO(forDate, { representation: 'date' }),
      },
    },
    notifyOnNetworkStatusChange: true,
  });
  const timesheetStatusCrewMembers: TimesheetStatusCrewMember[] = get(data, 'timesheetStatusBySupervisor', []);

  const getFilteredTimesheetStatusCrewMembers = (searchParam) => {
    const searchValue = toLower(trim(searchParam));
    if (searchValue.length >= 0 && searchValue.length < 3) return timesheetStatusCrewMembers;
    let filteredTimesheetStatusCrewMemberList: TimesheetStatusCrewMember[] = [];
    timesheetStatusCrewMembers.forEach((gfItem) => {
      if (gfItem) {
        const gfName = toLower(`${gfItem.crewMember?.firstName} ${gfItem.crewMember?.lastName}`);
        const gfCrewNumber = toLower(gfItem.crewMember?.crew?.crewNumber);
        if (includes(gfName, searchValue) || includes(gfCrewNumber, searchValue)) {
          const isGfAdded = some(filteredTimesheetStatusCrewMemberList, (item) => {
            return item?.crewMember?.id === gfItem.crewMember?.id;
          });
          if (!isGfAdded) filteredTimesheetStatusCrewMemberList.push(gfItem);
        }

        gfItem.directReports.forEach((directReport) => {
          if (directReport) {
            const directReportName = toLower(
              `${directReport.crewMember?.firstName} ${directReport.crewMember?.lastName}`
            );
            const directReportCrewNumber = toLower(directReport.crewMember?.crew?.crewNumber);
            if (includes(directReportName, searchValue) || includes(directReportCrewNumber, searchValue)) {
              const isGfAdded = some(filteredTimesheetStatusCrewMemberList, (item) => {
                return item?.crewMember?.id === gfItem.crewMember?.id;
              });
              if (!isGfAdded) {
                filteredTimesheetStatusCrewMemberList.push({
                  crewMember: gfItem.crewMember,
                  directReports: [directReport],
                });
              } else {
                const addedGfItem = find(filteredTimesheetStatusCrewMemberList, (item) => {
                  return item.crewMember?.id === gfItem.crewMember?.id;
                });
                if (addedGfItem && addedGfItem.directReports) {
                  const isDirectReportAdded = some(addedGfItem.directReports, (item) => {
                    return item?.crewMember?.id === directReport.crewMember?.id;
                  });
                  if (!isDirectReportAdded) {
                    const newGfItem: TimesheetStatusCrewMember = {
                      crewMember: addedGfItem.crewMember,
                      directReports: addedGfItem.directReports
                        ? [...addedGfItem.directReports, directReport]
                        : [directReport],
                    };
                    const updatedCrewList = filteredTimesheetStatusCrewMemberList.map((item) => {
                      return item?.crewMember?.id === addedGfItem?.crewMember?.id ? newGfItem : item;
                    });
                    filteredTimesheetStatusCrewMemberList = [...updatedCrewList];
                  }
                }
              }
            }
          }
        });
      }
    });
    return filteredTimesheetStatusCrewMemberList;
  };

  const filteredTimesheetStatusCrewMembers = getFilteredTimesheetStatusCrewMembers(searchQuery);
  const totalCrews = filteredTimesheetStatusCrewMembers.length;
  return (
    <CrewListContainerStyled>
      {showDateHeader && (
        <DateHeaderStyled>
          <DateHeader selectedDate={forDate} onDateSelect={(date) => setForDate(date)} />
        </DateHeaderStyled>
      )}
      <CrewHeadingStyled>
        <Text size="400" weight="600" family="graphik" color="grey.1000">
          <Trans>My Crew</Trans> {totalCrews > 0 ? `(${totalCrews})` : ``}
        </Text>
      </CrewHeadingStyled>
      {user?.crewMember.persona === PersonaType.SUPERINTENDENT && (
        <Box px={{ sm: '400' }} mt={{ sm: '550' }} mb={{ sm: '500' }}>
          <SearchField
            iconSize={IconSize.MEDIUM}
            placeholder={t`Search name or crew number`}
            type="search"
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
          />
        </Box>
      )}
      {(networkStatus === NetworkStatus.refetch || loading) && (
        <Box
          p={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          css={timesheetStatusCrewMembers.length === 0 ? 'height: 100%;' : ''}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box pb={{ sm: '500' }}>
            <Text>
              <Trans>There was a problem loading Crews. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => refetch()}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}
      {called && !loading && !error && filteredTimesheetStatusCrewMembers.length === 0 && (
        <Box
          flexDirection={{ sm: 'column' }}
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
        >
          <Heading size="400" weight="600" color="grey.600">
            <Trans> No crews found.</Trans>
          </Heading>
          <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
            <Text
              size="400"
              family="graphik"
              color="grey.600"
              //  line height is required because when sentence wraps in two lines gap between both lines are required.
              style={{ textAlign: 'center', lineHeight: '1.57' }}
            >
              <Trans> Try changing your search to view a crew.</Trans>
            </Text>
          </Box>
          <FilterIcon style={{ width: '124px', height: '110px' }} />
        </Box>
      )}
      {filteredTimesheetStatusCrewMembers.length > 0 && (
        <CrewListStyled>
          {user?.crewMember.persona === PersonaType.SUPERINTENDENT && (
            <>
              {map(filteredTimesheetStatusCrewMembers, (timesheetStatusCrewMemberItem: TimesheetStatusCrewMember) => {
                return (
                  <TimesheetCrewSupervisorItem
                    timesheetStatusCrewMember={timesheetStatusCrewMemberItem}
                    key={timesheetStatusCrewMemberItem.crewMember?.id}
                  />
                );
              })}
            </>
          )}
          {user?.crewMember.persona === PersonaType.GENERAL_FOREMAN && (
            <>
              {map(filteredTimesheetStatusCrewMembers, (timesheetStatusCrewMemberItem: TimesheetStatusCrewMember) => {
                return (
                  <TimesheetCrewItem
                    timesheetStatusCrewMember={timesheetStatusCrewMemberItem}
                    key={timesheetStatusCrewMemberItem.crewMember?.id}
                  />
                );
              })}
            </>
          )}
        </CrewListStyled>
      )}
    </CrewListContainerStyled>
  );
};
